/**
 * What: Renders a Polaris App Home empty state carrying a TestFeed house illustration at a
 * shared, bounded size.
 * Transport / data-flow: Pure presentation over caller-supplied copy and a build-time asset URL; no fetching.
 *
 * There's no `s-empty-state` component in @shopify/polaris-types yet, so this is composed from
 * `s-stack`/`s-box`/`s-image`/`s-heading`/`s-button` — same "hand-build the missing primitive"
 * approach as the bar in CreditUsageBar.tsx. The figure is capped at 480px wide, the shared
 * empty-state illustration size; `inlineSize="100%"` with `maxInlineSize` lets it
 * shrink below that on narrow viewports instead of overflowing. `aspectRatio="12/7"`
 * with `objectFit="contain"` keeps the 480x280 landscape frame consistent across art
 * delivered at different native ratios — contain letterboxes rather than crops, since
 * none of these assets are composed for a landscape frame.
 */
import type { ReactNode } from 'react'

export type HouseEmptyStateAction = {
  content: string
  onAction?: () => void
  url?: string
  disabled?: boolean
}

export function HouseEmptyState({
  heading,
  image,
  action,
  secondaryAction,
  children,
}: {
  heading: string
  image: string
  action?: HouseEmptyStateAction
  /** A quieter second choice beside the primary action, e.g. going back. */
  secondaryAction?: HouseEmptyStateAction
  children: ReactNode
}) {
  return (
    <s-stack direction="block" gap="large" alignItems="center">
      <s-box inlineSize="100%" maxInlineSize="480px">
        <s-image src={image} alt="" aspectRatio="12/7" objectFit="contain" inlineSize="fill" />
      </s-box>
      <s-stack direction="block" gap="base" alignItems="center">
        <s-heading>{heading}</s-heading>
        {children}
        {(action || secondaryAction) && (
          <s-stack direction="inline" gap="base" justifyContent="center">
            {action && <HouseEmptyStateButton action={action} variant="primary" />}
            {secondaryAction && <HouseEmptyStateButton action={secondaryAction} variant="secondary" />}
          </s-stack>
        )}
      </s-stack>
    </s-stack>
  )
}

function HouseEmptyStateButton({
  action,
  variant,
}: {
  action: HouseEmptyStateAction
  variant: 'primary' | 'secondary'
}) {
  return (
    <s-button
      variant={variant}
      href={action.url}
      onClick={action.url ? undefined : action.onAction}
      disabled={action.disabled}
    >
      {action.content}
    </s-button>
  )
}
